import React, { ReactNode, useState, FC } from 'react';

import PropTypes from 'prop-types';
import { CSSProp } from 'styled-components';

import { BasicButton } from '@adsk/alloy-react-button';
import {
  TriangleUpIcon,
  TriangleDownIcon,
  GearIcon,
  RenderIconType,
} from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import { Flyout } from '@adsk/alloy-react-tooltip';
import { useSideNavigation } from '@adsk/alloy-side-navigation';

import ProductLoader from './ProductLoader';
import ProductPicker from './ProductPicker';
import { productsListShape } from '../helpers/propTypes';
import { Product } from '../helpers/types';

// TODO - can be removed when account entitlement is available in V2
// TODO - verify it works as expected
const getProductIconV1 = (product: Product) =>
  product?.renderIcon?.({
    size: 24,
    style: { color: theme.colors.adskBlue500 },
  });

type ProductButtonProps = {
  productId?: string;
  required?: boolean;
  products?: Product[];
  renderCustomHeader?: () => ReactNode;
  onProductClick?: ({ id, url }: { [key: string]: string }) => void;
};

const renderGearIcon: RenderIconType = (p) => <GearIcon {...p} />;

/**
 * Acs TopNavigation
 */
const ProductButton: FC<ProductButtonProps> = ({
  products,
  productId,
  required,
  renderCustomHeader,
  onProductClick,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const { collapsed } = useSideNavigation();
  const [menuOpen, setMenuOpen] = useState(false);
  const PLACEHOLDER = {
    name: T.translate('PRODUCTS') as string,
    renderIcon: renderGearIcon,
    iconUrl: '',
    key: '',
    defaultUrl: '',
  };

  const currentProduct =
    products?.find((product) => product.key === productId) ||
    (!required && PLACEHOLDER);

  const TriangleIconComponent = menuOpen ? TriangleUpIcon : TriangleDownIcon;

  return currentProduct ? (
    <Flyout
      show={menuOpen}
      onHide={() => setMenuOpen(false)}
      onShow={() => setMenuOpen(true)}
      offset={[0, 15]}
      containerPadding={0}
      placement={Flyout.PLACEMENTS.BOTTOM_START}
      content={
        <ProductPicker
          selectedId={productId}
          products={products}
          renderCustomHeader={renderCustomHeader}
          onClose={() => setMenuOpen(false)}
          onProductClick={onProductClick}
        />
      }
    >
      <BasicButton
        onClick={() => setMenuOpen(!menuOpen)}
        data-testid="TopNavigation__products"
        style={
          {
            padding: 0,
            width: !collapsed && 195,
            justifyContent: 'start',
            color: theme.colors.charcoal900,
            '&:hover': {
              color: theme.colors.charcoal700,
            },
            [theme.mediaQuery.small]: {
              width: 'auto',
            },
          } as CSSProp
        }
      >
        <span
          css={[
            {
              display: 'inline-flex',
              borderRadius: 4,
              width: 32,
              height: 32,
              background: theme.colors.adskBlue100,
              alignItems: 'center',
              justifyContent: 'center',
            },
          ]}
        >
          {currentProduct.iconUrl ? (
            <span
              css={[
                {
                  maskImage: `url(${currentProduct.iconUrl})`,
                  backgroundColor: theme.colors.adskBlue500,
                  width: 24,
                  height: 24,
                },
              ]}
            />
          ) : (
            getProductIconV1(currentProduct)
          )}
        </span>
        {!collapsed && (
          <span
            css={[
              currentProduct.name?.length > 15
                ? { ...theme.typography.bodySmallBold, textAlign: 'left' }
                : theme.typography.bodyMediumBold,
              {
                maxWidth: 250,
                paddingLeft: '8px',
                [theme.mediaQuery.small]: {
                  display: 'none',
                },
              },
            ]}
          >
            {currentProduct.name}
          </span>
        )}
        <TriangleIconComponent
          style={{
            color: theme.colors.charcoal700,
            margin: '0 4px',
            [theme.mediaQuery.small]: {
              visibility: 'hidden',
            },
          }}
          size={20}
        />
      </BasicButton>
    </Flyout>
  ) : (
    <ProductLoader />
  );
};

ProductButton.displayName = 'ProductButton';

ProductButton.defaultProps = {
  required: true,
};

ProductButton.propTypes = {
  products: productsListShape,
  productId: PropTypes.string,
  required: PropTypes.bool,
  renderCustomHeader: PropTypes.func,
  onProductClick: PropTypes.func,
};

export default ProductButton;
